import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, api, type UiTheme, type User, type UserProfileUpdate } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { InfoHint } from '../components/InfoHint';
import { ToggleSwitch } from '../components/ToggleSwitch';
import { clearStoredTableStates } from '../lib/tableState';

export function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [email, setEmail] = useState('');
  const [telegram, setTelegram] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyTelegram, setNotifyTelegram] = useState(false);
  const [notifySuccess, setNotifySuccess] = useState(true);
  const [notifyFailure, setNotifyFailure] = useState(true);
  const [persistTableSorting, setPersistTableSorting] = useState(false);
  const [persistTableFilters, setPersistTableFilters] = useState(false);
  const [showUiHints, setShowUiHints] = useState(true);
  const [uiTheme, setUiTheme] = useState<UiTheme>('dark');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    setEmail(user.email ?? '');
    setTelegram(user.telegram_chat_id ?? '');
    setNotifyEmail(user.notify_email_enabled);
    setNotifyTelegram(user.notify_telegram_enabled);
    setNotifySuccess(user.notify_on_success);
    setNotifyFailure(user.notify_on_failure);
    setPersistTableSorting(user.persist_table_sorting);
    setPersistTableFilters(user.persist_table_filters);
    setShowUiHints(user.show_ui_hints);
    setUiTheme(user.ui_theme);
  }, [user]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    setError(null);
    const body: UserProfileUpdate = {
      email: email.trim() || null,
      telegram_chat_id: telegram.trim() || null,
      notify_email_enabled: notifyEmail,
      notify_telegram_enabled: notifyTelegram,
      notify_on_success: notifySuccess,
      notify_on_failure: notifyFailure,
      persist_table_sorting: persistTableSorting,
      persist_table_filters: persistTableFilters,
      show_ui_hints: showUiHints,
      ui_theme: uiTheme,
    };
    try {
      const updated = await api.put<User>('/api/users/me', body);
      if (user?.id) {
        if (!body.persist_table_sorting) {
          clearStoredTableStates(window.localStorage, {
            userId: user.id,
            buckets: ['sorting'],
          });
        }
        if (!body.persist_table_filters) {
          clearStoredTableStates(window.localStorage, {
            userId: user.id,
            buckets: ['filters'],
          });
        }
      }
      setMessage('Профиль сохранён');
      await refreshUser();
      setEmail(updated.email ?? '');
      setTelegram(updated.telegram_chat_id ?? '');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="page-header">
        <div className="rubric">Аккаунт</div>
        <h1 className="text-h1 text-text-primary font-strong">Профиль</h1>
      </div>

      {message ? <div className="pill-success inline-block">{message}</div> : null}
      {error ? <div className="error-hint">{error}</div> : null}

      <form onSubmit={save} className="card card-padding space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <span className="form-label">Логин</span>
            <div className="text-text-primary mt-1">{user?.username}</div>
          </div>
          <div>
            <span className="form-label">Роль</span>
            <div className="mt-1">
              <span className="pill-neutral">{user?.role}</span>
            </div>
          </div>
        </div>

        <label className="block">
          <span className="form-label">Email</span>
          <input
            className="input mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
          />
        </label>

        <label className="block">
          <span className="form-label">Telegram chat id</span>
          <input
            className="input mt-1"
            value={telegram}
            onChange={(e) => setTelegram(e.target.value)}
            placeholder="123456789"
          />
        </label>

        <div className="divider" />

        <div className="space-y-3">
          <h2 className="inline-flex items-center gap-2 section-title">
            Уведомления
            <InfoHint text="Настройки определяют, какие события прогонов отправлять на email и Telegram." />
          </h2>
          <ToggleSwitch checked={notifyEmail} onChange={setNotifyEmail} label="Email" />
          <ToggleSwitch checked={notifyTelegram} onChange={setNotifyTelegram} label="Telegram" />
          <ToggleSwitch
            checked={notifySuccess}
            onChange={setNotifySuccess}
            label="Успешный прогон"
          />
          <ToggleSwitch
            checked={notifyFailure}
            onChange={setNotifyFailure}
            label="Ошибка прогона"
          />
        </div>

        <div className="divider" />

        <div className="space-y-3">
          <h2 className="inline-flex items-center gap-2 section-title">
            Интерфейс
            <InfoHint text="Эти параметры меняют только ваш интерфейс и не влияют на настройки других пользователей." />
          </h2>
          <ToggleSwitch
            checked={uiTheme === 'light'}
            onChange={(checked) => setUiTheme(checked ? 'light' : 'dark')}
            label="Светлая тема"
            description="Сохраняется в профиле и применяется после входа на любом устройстве."
          />
          <ToggleSwitch
            checked={showUiHints}
            onChange={setShowUiHints}
            label="Показывать подсказки"
            description="Иконки «?» рядом с полями и разделами. Можно отключить, когда интерфейс уже знаком."
          />
        </div>

        <div className="divider" />

        <div className="space-y-3">
          <h2 className="inline-flex items-center gap-2 section-title">
            Таблицы
            <InfoHint text="Можно сохранять сортировку, пагинацию и фильтры таблиц между входами на этом устройстве." />
          </h2>
          <ToggleSwitch
            checked={persistTableSorting}
            onChange={setPersistTableSorting}
            label="Сохранять сортировку и пагинацию"
            description="Восстанавливает порядок, направление, страницу и размер страницы после входа."
          />
          <ToggleSwitch
            checked={persistTableFilters}
            onChange={setPersistTableFilters}
            label="Сохранять фильтры таблиц"
            description="При следующем входе поля фильтров останутся такими же."
          />
        </div>

        <button type="submit" className="btn-primary btn-md" disabled={saving}>
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
      </form>
    </div>
  );
}
